import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, RadialBarChart, RadialBar
} from 'recharts';
import {
  FileText, AlertTriangle, Clock, CheckCircle, TrendingUp, 
  Building2, Shield, ChevronRight, Eye, Target, Calendar,
  ArrowUpRight, ArrowDownRight, Users, Briefcase, Scale
} from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#1d4ed8', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#8B5CF6'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900/95 backdrop-blur-sm px-4 py-3 rounded-xl shadow-2xl border border-slate-700/50">
        <p className="text-white/70 text-xs font-medium uppercase tracking-wider mb-1">{label || payload[0]?.name}</p>
        <p className="text-white text-lg font-bold">{payload[0].value}</p>
      </div>
    );
  }
  return null;
};

export const CasaCivilDashboard = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [acts, setActs] = useState([]);
  const [secretariasData, setSecretariasData] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, actsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/acts')
      ]);
      
      setStats(statsRes.data);
      setActs(actsRes.data || []);
      
      // Group acts by secretaria
      const bySecretaria = (actsRes.data || []).reduce((acc, act) => {
        const sec = act.secretaria_responsavel || 'Não atribuído';
        if (!acc[sec]) acc[sec] = { total: 0, pendentes: 0, atrasados: 0 };
        acc[sec].total++;
        if (act.status === 'PENDENTE' || act.status === 'EM_ANDAMENTO') acc[sec].pendentes++;
        if (act.status === 'ATRASADO') acc[sec].atrasados++;
        return acc;
      }, {});
      
      setSecretariasData(Object.entries(bySecretaria).map(([name, data]) => ({
        name: name.length > 20 ? name.substring(0, 20) + '...' : name,
        ...data
      })).sort((a, b) => b.total - a.total).slice(0, 8));
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const criticalActs = acts.filter(a => a.status === 'ATRASADO' || a.prioridade === 'CRITICA').slice(0, 5);
  const pendingApprovals = acts.filter(a => a.status === 'AGUARDANDO_APROVACAO').slice(0, 5);

  const riskDistribution = [
    { name: 'Crítico', value: acts.filter(a => a.nivel_risco === 'CRITICO').length, color: '#EF4444' },
    { name: 'Alto', value: acts.filter(a => a.nivel_risco === 'ALTO').length, color: '#F59E0B' },
    { name: 'Médio', value: acts.filter(a => a.nivel_risco === 'MEDIO').length, color: '#3B82F6' },
    { name: 'Baixo', value: acts.filter(a => a.nivel_risco === 'BAIXO' || !a.nivel_risco).length, color: '#10B981' },
  ];

  const governanceScore = stats ? Math.round(
    ((stats.concluidos || 0) / Math.max(stats.total || 1, 1)) * 100
  ) : 0;

  if (loading) {
    return (
      <AppLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 space-y-6" data-testid="casa-civil-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Dashboard Casa Civil</h1>
            <p className="text-slate-600">Visão estratégica para coordenação governamental</p>
          </div>
          <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
            <Building2 className="w-4 h-4 mr-1" />
            Casa Civil
          </Badge>
        </div>

        {/* Governance Score Card */}
        <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Índice de Governança</p>
                <p className="text-4xl font-bold mt-1">{governanceScore}%</p>
                <p className="text-slate-400 text-sm mt-2">
                  {stats?.concluidos || 0} de {stats?.total || 0} atos concluídos
                </p>
              </div>
              <div className="w-32 h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="100%" data={[{ value: governanceScore }]}>
                    <RadialBar dataKey="value" fill="#1d4ed8" background={{ fill: '#374151' }} cornerRadius={10} />
                  </RadialBarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Atos Críticos</p>
                  <p className="text-2xl font-bold text-red-600">{stats?.atrasados || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-200" />
              </div>
              <p className="text-xs text-slate-500 mt-2">Requerem atenção imediata</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Aguardando Aprovação</p>
                  <p className="text-2xl font-bold text-amber-600">{pendingApprovals.length}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-200" />
              </div>
              <p className="text-xs text-slate-500 mt-2">Pendentes de decisão</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Secretarias Envolvidas</p>
                  <p className="text-2xl font-bold text-blue-600">{secretariasData.length}</p>
                </div>
                <Building2 className="w-8 h-8 text-blue-200" />
              </div>
              <p className="text-xs text-slate-500 mt-2">Com atos em andamento</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Taxa de Conformidade</p>
                  <p className="text-2xl font-bold text-green-600">{governanceScore}%</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-200" />
              </div>
              <p className="text-xs text-slate-500 mt-2">Atos no prazo</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Secretarias Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Desempenho por Secretaria
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={secretariasData} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total" fill="#1d4ed8" radius={[0, 4, 4, 0]} name="Total" />
                  <Bar dataKey="atrasados" fill="#EF4444" radius={[0, 4, 4, 0]} name="Atrasados" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Risk Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-600" />
                Distribuição de Risco
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={riskDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {riskDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 mt-4">
                {riskDistribution.map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-xs text-slate-600">{item.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Critical Acts & Pending Approvals */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Critical Acts */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Atos Críticos
              </CardTitle>
              <Button variant="ghost" size="sm">
                Ver todos <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {criticalActs.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Nenhum ato crítico no momento</p>
              ) : (
                <div className="space-y-3">
                  {criticalActs.map((act, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-100">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{act.numero_ato}</p>
                        <p className="text-xs text-slate-600">{act.orgao_origem}</p>
                      </div>
                      <Badge variant="destructive" className="text-xs">
                        {act.status?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Approvals */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-amber-600">
                <Clock className="w-5 h-5" />
                Aguardando Aprovação
              </CardTitle>
              <Button variant="ghost" size="sm">
                Ver todos <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {pendingApprovals.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Nenhuma aprovação pendente</p>
              ) : (
                <div className="space-y-3">
                  {pendingApprovals.map((act, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-100">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{act.numero_ato}</p>
                        <p className="text-xs text-slate-600">{act.secretaria_responsavel || 'Não atribuído'}</p>
                      </div>
                      <Button size="sm" variant="outline" className="text-xs">
                        Revisar
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
};

export default CasaCivilDashboard;
